import { useCallback, useEffect, useMemo, useState } from "react";
import {
  calculateEstimatedCost,
  type EstimatedCostReport,
  type MonthlyUsageStatus,
  type UsageGroup,
  type UsageLimit,
  type UsageRange,
  type UsageTotals,
} from "@racler/protocol";
import { api, errorText } from "../../lib/api.ts";
import { relativeTime } from "../../lib/chatGroups.ts";
import { formatCost, formatTokens } from "../../lib/format.ts";
import { navigate, projectPath } from "../../lib/router.ts";
import { useAsync } from "../../lib/useAsync.ts";

const RANGES: Array<[UsageRange, string]> = [
  ["7d", "Last 7 days"],
  ["30d", "Last 30 days"],
  ["all", "All time"],
];

export interface DirtyHandler {
  isDirty: boolean;
  save: () => Promise<void> | void;
  discard?: () => void;
}

/** Usage from provider-reported metadata only. No prices are guessed: missing cost shows as "not reported". */
export function UsageSection({
  onDirtyChange,
}: {
  onDirtyChange?: (handler: DirtyHandler | null) => void;
} = {}) {
  const [range, setRange] = useState<UsageRange>("30d");
  const { data, error, loading } = useAsync(() => api.usage(range), range);

  const [limitsData, setLimitsData] = useState<{
    limits: UsageLimit[];
    status: {
      account: MonthlyUsageStatus;
    };
  } | null>(null);
  const [hardCost, setHardCost] = useState("");
  const [warningCost, setWarningCost] = useState("");
  const [hardTokens, setHardTokens] = useState("");
  const [initialLimits, setInitialLimits] = useState({ warningCost: "", hardCost: "", hardTokens: "" });
  const [savingLimit, setSavingLimit] = useState(false);
  const [limitSavedMsg, setLimitSavedMsg] = useState<string | null>(null);

  const loadLimits = useCallback(async () => {
    try {
      const res = await api.usageLimits();
      setLimitsData(res);
      const accLimit = res.limits.find((l) => l.scopeType === "account");
      if (accLimit) {
        const hc = accLimit.hardCostUsd !== undefined ? String(accLimit.hardCostUsd) : "";
        const wc = accLimit.warningCostUsd !== undefined ? String(accLimit.warningCostUsd) : "";
        const ht = accLimit.hardTokens !== undefined ? String(accLimit.hardTokens) : "";
        setHardCost(hc);
        setWarningCost(wc);
        setHardTokens(ht);
        setInitialLimits({ warningCost: wc, hardCost: hc, hardTokens: ht });
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void loadLimits();
  }, [loadLimits]);

  const isDirty = useMemo(() => {
    return (
      warningCost !== initialLimits.warningCost ||
      hardCost !== initialLimits.hardCost ||
      hardTokens !== initialLimits.hardTokens
    );
  }, [warningCost, hardCost, hardTokens, initialLimits]);

  const saveLimits = useCallback(async () => {
    setSavingLimit(true);
    setLimitSavedMsg(null);
    try {
      await api.saveUsageLimit({
        scopeType: "account",
        hardCostUsd: hardCost ? parseFloat(hardCost) : undefined,
        warningCostUsd: warningCost ? parseFloat(warningCost) : undefined,
        hardTokens: hardTokens ? parseInt(hardTokens, 10) : undefined,
      });
      setLimitSavedMsg("Limits saved successfully");
      setInitialLimits({ warningCost, hardCost, hardTokens });
      void loadLimits();
    } catch (e) {
      alert(`Failed to save: ${errorText(e)}`);
      throw e;
    } finally {
      setSavingLimit(false);
    }
  }, [hardCost, warningCost, hardTokens, loadLimits]);

  const discardLimits = useCallback(() => {
    setWarningCost(initialLimits.warningCost);
    setHardCost(initialLimits.hardCost);
    setHardTokens(initialLimits.hardTokens);
  }, [initialLimits]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange({
        isDirty,
        save: saveLimits,
        discard: discardLimits,
      });
    }
  }, [isDirty, onDirtyChange, saveLimits, discardLimits]);

  const handleSaveLimits = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveLimits();
  };

  const estimatedCost = useMemo(() => {
    if (!data?.byModel) return null;
    return calculateEstimatedCost(data.byModel);
  }, [data?.byModel]);

  return (
    <>
      <h1>Usage</h1>
      <div className="range">
        {RANGES.map(([r, label]) => (
          <button key={r} className={range === r ? "active" : ""} onClick={() => setRange(r)}>
            {label}
          </button>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
      {loading && !data && <p className="muted">Loading…</p>}
      {data && data.totals.responses === 0 && (
        <p className="muted" data-testid="usage-empty">
          No usage recorded in this period. Usage is captured when a provider reports token counts.
        </p>
      )}
      {data && data.totals.responses > 0 && (
        <div data-testid="usage">
          <div className="stat-row">
            <Stat label="Responses" value={String(data.totals.responses)} />
            <Stat label="Input tokens" value={formatTokens(data.totals.inputTokens)} />
            <Stat label="Output tokens" value={formatTokens(data.totals.outputTokens)} />
            {estimatedCost && <EstimatedCostStat summary={estimatedCost} />}
            <CostStat t={data.totals} />
          </div>
          <h3>By provider</h3>
          <Table
            rows={data.byProvider.map((r) => ({
              key: r.provider,
              name: r.provider,
              t: r,
              estimatedCostUsd: r.estimatedCostUsd ?? estimatedCost?.byProviderCost.get(r.provider) ?? null,
            }))}
          />
          <h3>By model</h3>
          <Table
            rows={data.byModel.map((r: UsageGroup) => ({
              key: `${r.provider}/${r.model}`,
              name: r.model,
              sub: r.provider,
              t: r,
              estimatedCostUsd: r.estimatedCostUsd ?? estimatedCost?.byModelCost.get(`${r.provider}/${r.model}`) ?? null,
            }))}
          />
          <h3>Recent chats</h3>
          <ul className="list">
            {data.recentSessions.map((s) => (
              <li key={s.sessionId}>
                <button
                  className="list-row"
                  onClick={() => navigate(projectPath(s.machineId, s.projectId, s.sessionId))}
                >
                  <span className="row-main">{s.title}</span>
                  <span className="muted small">
                    {formatTokens(s.inputTokens + s.outputTokens)} tokens · {formatCost(s).text}
                  </span>
                  <span className="muted small">{relativeTime(s.lastAt)}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="muted small">
            Estimated cost is derived from token usage and model catalog pricing. Reported cost is the sum of costs
            authoritatively reported by providers.
          </p>
        </div>
      )}

      <section className="set-block" data-testid="usage-limits">
        <h3>Monthly Practical Usage Limits</h3>
        <p className="muted small">
          Configure safety ceilings for your account. When a hard limit is reached, new model requests are
          blocked before they reach the provider. In-flight streams and non-AI features are never killed.
        </p>

        {limitsData?.status.account && (
          <div className="limit-status-box" style={{ marginBottom: "1rem" }}>
            <p>
              <strong>Current Month ({limitsData.status.account.period}):</strong>{" "}
              ${limitsData.status.account.currentCostUsd.toFixed(4)} ·{" "}
              {formatTokens(limitsData.status.account.currentTokens)} tokens
            </p>
            {limitsData.status.account.costIncomplete && (
              <p className="warning small" style={{ color: "#d97706" }}>
                Cost coverage is partial: some responses did not report cost from the provider. Token limits apply.
              </p>
            )}
            {limitsData.status.account.hardLimitExceeded && (
              <p className="error small" style={{ color: "#dc2626" }}>
                <strong>Hard limit exceeded:</strong> new model requests are currently blocked.
              </p>
            )}
            {!limitsData.status.account.hardLimitExceeded && limitsData.status.account.warningTriggered && (
              <p className="warning small" style={{ color: "#d97706" }}>
                <strong>Warning:</strong> you have reached your configured warning threshold.
              </p>
            )}
          </div>
        )}

        <form onSubmit={(e) => { void handleSaveLimits(e); }}>
          <div className="form-row">
            <label>Warning Cost Ceiling (USD)</label>
            <input
              type="number"
              step="0.01"
              placeholder="e.g. 10.00"
              value={warningCost}
              onChange={(e) => setWarningCost(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Hard Stop Cost Ceiling (USD)</label>
            <input
              type="number"
              step="0.01"
              placeholder="e.g. 25.00"
              value={hardCost}
              onChange={(e) => setHardCost(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Hard Stop Token Ceiling</label>
            <input
              type="number"
              step="1000"
              placeholder="e.g. 500000"
              value={hardTokens}
              onChange={(e) => setHardTokens(e.target.value)}
            />
          </div>
          <div style={{ marginTop: "1rem" }}>
            <button type="submit" className="action-btn" disabled={savingLimit}>
              {savingLimit ? "Saving…" : "Save Limits"}
            </button>
            {limitSavedMsg && <span className="small" style={{ marginLeft: "1rem", color: "#16a34a" }}>{limitSavedMsg}</span>}
          </div>
        </form>
      </section>

    </>
  );
}

const Stat = ({
  label,
  value,
  subtext,
  testId,
}: {
  label: string;
  value: string;
  subtext?: string;
  testId?: string;
}) => (
  <div className="stat" data-testid={testId}>
    <div className="stat-value">{value}</div>
    <div className="muted small">{label}</div>
    {subtext && (
      <div
        className="muted small text-warn"
        style={{ fontSize: "0.75rem", marginTop: "2px", color: "var(--color-warning, #d97706)" }}
      >
        {subtext}
      </div>
    )}
  </div>
);

function EstimatedCostStat({ summary }: { summary: EstimatedCostReport & { hasKnownCost?: boolean } }) {
  const { totalUsd, unknownModelCount } = summary;
  const isPartial = unknownModelCount > 0;

  let valueText: string;
  let subtext: string | undefined;

  if (summary.hasKnownCost === false && isPartial) {
    valueText = "Unavailable";
    subtext = `Excludes usage from ${unknownModelCount} model${unknownModelCount === 1 ? "" : "s"} without pricing data.`;
  } else {
    valueText = totalUsd < 0.01 && totalUsd > 0 ? `$${totalUsd.toFixed(4)}` : `$${totalUsd.toFixed(2)}`;
    if (isPartial) {
      subtext = `Excludes usage from ${unknownModelCount} model${unknownModelCount === 1 ? "" : "s"} without pricing data.`;
    }
  }

  return (
    <Stat
      label="Estimated cost"
      value={valueText}
      subtext={subtext}
      testId="estimated-cost-stat"
    />
  );
}

function CostStat({ t }: { t: UsageTotals }) {
  const c = formatCost(t);
  return <Stat label={c.partial ? "Reported cost (partial)" : "Reported cost"} value={c.text} />;
}

function Table({
  rows,
}: {
  rows: Array<{
    key: string;
    name: string;
    sub?: string;
    t: UsageTotals;
    estimatedCostUsd?: number | null;
  }>;
}) {
  return (
    <table className="kv usage-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Responses</th>
          <th>Input</th>
          <th>Output</th>
          <th>Estimated cost</th>
          <th>Reported cost</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const c = formatCost(r.t);
          return (
            <tr key={r.key}>
              <td>
                {r.name} {r.sub && <span className="muted small">{r.sub}</span>}
              </td>
              <td>{r.t.responses}</td>
              <td>{formatTokens(r.t.inputTokens)}</td>
              <td>{formatTokens(r.t.outputTokens)}</td>
              <td>
                {r.estimatedCostUsd !== undefined && r.estimatedCostUsd !== null
                  ? `$${r.estimatedCostUsd < 0.01 && r.estimatedCostUsd > 0 ? r.estimatedCostUsd.toFixed(4) : r.estimatedCostUsd.toFixed(2)}`
                  : "—"}
              </td>
              <td>
                {c.text}
                {c.partial ? " *" : ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
